import { CreditCheckStatus } from '@/pages/creditCheck/creditCheckHelper';
import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { failedResponses } from '../errors/responses';
import { Company } from './companies';

const c = initContract();

export const CreditChecker = z.object({
  id: z.string(),
  name: z.string(),
  code: z.string(),
  disabled: z.boolean()
});

export const CreditCheckEffectiveDate = z.object({
  value: z.string(),
  invalidatedOn: z.string(),
  createdOn: z.string(),
  userId: z.string()
});

export const CreditCheck = z.object({
  id: z.string(),
  creditLimit: z.number(),
  effectiveDate: CreditCheckEffectiveDate
});

export const MspCreditCheck = CreditCheck.merge(
  z.object({
    mspId: z.string()
  })
);

export const CustomerCreditCheck = CreditCheck.merge(
  z.object({
    customerId: z.string(),
    status: z.nativeEnum(CreditCheckStatus)
  })
);
export type CustomerCreditCheck = z.infer<typeof CustomerCreditCheck>;

export const CreditCheckerCreditChecks = z.object({
  mspCustomerCreditChecks: z.array(
    z.object({
      mspId: z.string(),
      customerCreditChecks: z.array(
        z.object({
          customerId: z.string(),
          creditCheck: CustomerCreditCheck
        })
      )
    })
  )
});

export type CreditCheckerCreditChecks = z.infer<typeof CreditCheckerCreditChecks>;

export default c.router({
  getMspCreditChecks: {
    method: 'GET',
    path: '/mspcreditchecks',
    responses: {
      200: z.object({
        items: z.array(MspCreditCheck)
      }),
      ...failedResponses
    }
  },
  getCustomerCreditChecks: {
    method: 'GET',
    path: '/msp/:mspId/customercreditchecks',
    responses: {
      200: z.object({
        items: z.array(CustomerCreditCheck)
      }),
      ...failedResponses
    }
  },
  createMspCreditCheck: {
    method: 'POST',
    path: '/msp/:mspId/creditchecks',
    responses: {
      200: MspCreditCheck,
      ...failedResponses
    },
    body: z.object({
      creditLimit: z.number()
    })
  },
  createCustomerCreditCheck: {
    method: 'POST',
    path: '/msp/:mspId/customer/:customerId/creditchecks',
    responses: {
      200: CustomerCreditCheck,
      ...failedResponses
    },
    body: z.object({
      creditLimit: z.number(),
      status: z.nativeEnum(CreditCheckStatus)
    })
  },
  getMspCreditCheck: {
    method: 'GET',
    path: '/msp/:mspId/creditchecks/:id',
    responses: {
      200: MspCreditCheck,
      ...failedResponses
    }
  },
  getCustomerCreditCheck: {
    method: 'GET',
    path: '/msp/:mspId/customer/:customerId/creditchecks/:id',
    responses: {
      200: CustomerCreditCheck,
      ...failedResponses
    }
  },
  getMspCreditCheckHistory: {
    method: 'GET',
    path: '/msp/:mspId/creditchecks/history',
    responses: {
      200: z.object({
        items: z.array(MspCreditCheck)
      }),
      ...failedResponses
    }
  },
  getCustomerCreditCheckHistory: {
    method: 'GET',
    path: '/msp/:mspId/customer/:customerId/creditchecks/history',
    responses: {
      200: z.object({
        items: z.array(CustomerCreditCheck)
      }),
      ...failedResponses
    }
  },
  createCreditCheckCode: {
    method: 'POST',
    path: '/creditcheckers/code',
    body: z.object({
      names: z.array(z.string())
    }),
    responses: {
      200: c.type<{
        items: { name: string; code: string }[];
      }>(),
      ...failedResponses
    }
  },
  createCreditChecker: {
    method: 'POST',
    path: '/creditcheckers',
    body: z.object({
      name: z.string(),
      code: z.string()
    }),
    responses: {
      200: CreditChecker,
      ...failedResponses
    }
  },
  getCreditCheckers: {
    method: 'GET',
    path: '/creditcheckers',
    responses: {
      200: z.object({
        items: z.array(CreditChecker)
      }),
      ...failedResponses
    }
  },
  getCreditChecksForCreditChecker: {
    method: 'GET',
    path: '/creditcheckers/:creditCheckerId/creditchecks',
    responses: {
      200: CreditCheckerCreditChecks,
      ...failedResponses
    }
  },
  getCustomer: {
    method: 'GET',
    path: '/creditcheckers/:creditCheckerId/companies/:id',
    responses: {
      200: Company,
      ...failedResponses
    }
  }
});
